import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { PaginationState } from "@/hooks/usePagination";

interface Props {
  pagination: PaginationState;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];

export function PaginationControls({ pagination }: Props) {
  const { page, totalPages, totalCount, canPrev, canNext, prevPage, nextPage, from, pageSize, setPageSize, setPage } = pagination;
  const [pageInput, setPageInput] = useState("");

  const showingFrom = from + 1;
  const showingTo = Math.min(from + pageSize, totalCount);

  const isAll = !PAGE_SIZE_OPTIONS.includes(pageSize) && pageSize >= totalCount;

  const handlePageSizeChange = (value: string) => {
    if (value === "all") {
      setPageSize(Math.max(totalCount, 1));
    } else {
      setPageSize(Number(value));
    }
  };

  const handlePageJump = (val: string) => {
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) {
      setPage(n);
    }
    setPageInput("");
  };

  return (
    <div className="flex items-center justify-between pt-3">
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">
          Showing {showingFrom}–{showingTo} of {totalCount}
        </span>
        <select
          className="h-7 rounded-md border border-input bg-transparent px-2 text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          value={isAll ? "all" : pageSize}
          onChange={(e) => handlePageSizeChange(e.target.value)}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
          <option value="all">All</option>
        </select>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={!canPrev} onClick={prevPage}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground px-1">Page</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            placeholder={String(page)}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={(e) => handlePageJump(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handlePageJump((e.target as HTMLInputElement).value); }}
            className="h-7 w-12 rounded-md border border-input bg-transparent px-1 text-center text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <span className="text-xs text-muted-foreground px-1">of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={!canNext} onClick={nextPage}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}